import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Chip,
  Icon,
  Paper,
  Divider,
} from '@mui/material';
import {
  Dashboard,
  ShoppingCart,
  Inventory,
  TrendingUp,
  CalendarToday,
  Event,
  Assessment,
  People,
  Receipt,
  AccountCircle,
  Settings,
  Add,
  Edit,
  Delete,
  Refresh,
  Save,
  Search,
  Help,
} from '@mui/icons-material';
import { useShortcutsContext } from '../context/ShortcutsContext';
import { commandPaletteActions, normalizeShortcut } from '../data/shortcuts';

const ICON_MAP = {
  dashboard: <Dashboard fontSize="small" />,
  shopping_cart: <ShoppingCart fontSize="small" />,
  inventory: <Inventory fontSize="small" />,
  trending_up: <TrendingUp fontSize="small" />,
  calendar_today: <CalendarToday fontSize="small" />,
  event: <Event fontSize="small" />,
  assessment: <Assessment fontSize="small" />,
  people: <People fontSize="small" />,
  receipt: <Receipt fontSize="small" />,
  account_circle: <AccountCircle fontSize="small" />,
  settings: <Settings fontSize="small" />,
  add: <Add fontSize="small" />,
  edit: <Edit fontSize="small" />,
  delete: <Delete fontSize="small" />,
  refresh: <Refresh fontSize="small" />,
  save: <Save fontSize="small" />,
  search: <Search fontSize="small" />,
  help: <Help fontSize="small" />,
};

const CommandPalette = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { executeAction } = useShortcutsContext();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Fuzzy search function
  const fuzzyMatch = (str, pattern) => {
    const patternLower = pattern.toLowerCase();
    const strLower = str.toLowerCase();

    let patternIdx = 0;
    let score = 0;
    let consecutiveMatches = 0;

    for (let i = 0; i < strLower.length; i++) {
      if (patternIdx < patternLower.length && strLower[i] === patternLower[patternIdx]) {
        score += 1 + consecutiveMatches;
        consecutiveMatches++;
        patternIdx++;
      } else {
        consecutiveMatches = 0;
      }
    }

    return patternIdx === patternLower.length ? score : 0;
  };

  // Filter and sort actions based on search
  const filteredActions = useMemo(() => {
    if (!searchQuery.trim()) {
      return commandPaletteActions;
    }

    const query = searchQuery.trim();

    return commandPaletteActions
      .map(action => ({
        ...action,
        score: fuzzyMatch(action.label, query),
      }))
      .filter(action => action.score > 0)
      .sort((a, b) => b.score - a.score);
  }, [searchQuery]);

  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [filteredActions]);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      setSearchQuery('');
      setSelectedIndex(0);
    }
  }, [open]);

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current && selectedIndex >= 0) {
      const selectedElement = listRef.current.children[selectedIndex];
      selectedElement?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }
  }, [selectedIndex]);

  // Handle keyboard navigation
  const handleKeyDown = (event) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setSelectedIndex(prev =>
          prev < filteredActions.length - 1 ? prev + 1 : prev
        );
        break;

      case 'ArrowUp':
        event.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : 0));
        break;

      case 'Enter':
        event.preventDefault();
        if (filteredActions[selectedIndex]) {
          handleActionSelect(filteredActions[selectedIndex]);
        }
        break;

      case 'Escape':
        event.preventDefault();
        onClose();
        break;

      default:
        break;
    }
  };

  // Handle action selection
  const handleActionSelect = (action) => {
    if (action.path) {
      navigate(action.path);
    } else if (action.action) {
      executeAction(action.action);
    }

    onClose();
    setSearchQuery('');
  };

  // Group actions by type
  const groupedActions = useMemo(() => {
    const groups = {
      navigation: [],
      actions: [],
      search: [],
      help: [],
    };

    filteredActions.forEach(action => {
      if (action.path) {
        groups.navigation.push(action);
      } else if (action.action === 'search' || action.action === 'globalSearch') {
        groups.search.push(action);
      } else if (action.action === 'showHelp') {
        groups.help.push(action);
      } else {
        groups.actions.push(action);
      }
    });

    return Object.entries(groups).filter(([, items]) => items.length > 0);
  }, [filteredActions]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          position: 'fixed',
          top: 100,
          m: 0,
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* Search Input */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <TextField
            ref={inputRef}
            fullWidth
            placeholder="Type a command or search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            variant="outlined"
            autoComplete="off"
            InputProps={{
              startAdornment: (
                <Search sx={{ mr: 1, color: 'text.secondary' }} />
              ),
              sx: {
                fontSize: '1.1rem',
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
              },
            }}
          />
        </Box>

        {/* Results */}
        <Box sx={{ maxHeight: '60vh', overflow: 'auto' }}>
          {filteredActions.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                {searchQuery ? `No results for "${searchQuery}"` : 'Type to search commands...'}
              </Typography>
            </Box>
          ) : (
            <List ref={listRef} dense sx={{ py: 1 }}>
              {groupedActions.map(([groupName, actions], groupIndex) => (
                <Box key={groupName}>
                  {groupIndex > 0 && <Divider sx={{ my: 1 }} />}

                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      px: 2,
                      py: 0.5,
                      display: 'block',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      fontSize: '0.7rem',
                    }}
                  >
                    {groupName}
                  </Typography>

                  {actions.map((action, actionIndex) => {
                    const globalIndex = filteredActions.indexOf(action);
                    const isSelected = globalIndex === selectedIndex;

                    return (
                      <ListItem key={action.id} disablePadding>
                        <ListItemButton
                          selected={isSelected}
                          onClick={() => handleActionSelect(action)}
                          sx={{
                            py: 1.5,
                            '&.Mui-selected': {
                              backgroundColor: 'primary.main',
                              color: 'primary.contrastText',
                              '&:hover': {
                                backgroundColor: 'primary.dark',
                              },
                              '& .MuiListItemIcon-root': {
                                color: 'inherit',
                              },
                              '& .MuiChip-root': {
                                borderColor: 'currentColor',
                                color: 'inherit',
                              },
                            },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            {ICON_MAP[action.icon] || <Icon fontSize="small">{action.icon}</Icon>}
                          </ListItemIcon>
                          <ListItemText
                            primary={action.label}
                            primaryTypographyProps={{
                              fontWeight: isSelected ? 600 : 400,
                            }}
                          />
                          {action.shortcut && (
                            <Chip
                              label={normalizeShortcut(action.shortcut)}
                              size="small"
                              variant="outlined"
                              sx={{
                                height: 24,
                                fontSize: '0.7rem',
                                fontFamily: 'monospace',
                                fontWeight: 600,
                              }}
                            />
                          )}
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </Box>
              ))}
            </List>
          )}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            p: 1.5,
            borderTop: 1,
            borderColor: 'divider',
            display: 'flex',
            gap: 2,
            justifyContent: 'center',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Chip label="↑↓" size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
            <Typography variant="caption" color="text.secondary">
              Navigate
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Chip label="Enter" size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
            <Typography variant="caption" color="text.secondary">
              Select
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Chip label="Esc" size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
            <Typography variant="caption" color="text.secondary">
              Close
            </Typography>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CommandPalette;
